
import { first, takeUntil, filter, map, mergeMap } from 'rxjs/operators'
import * as numeral from 'numeral'
import * as moment from 'moment'
import * as omitBy from 'lodash/omitBy'
import { Observable, merge, of, combineLatest, EMPTY } from 'rxjs'
import { ActionsObservable, combineEpics } from 'redux-observable'
import * as sum from 'lodash/sum'
import { Store } from 'interfaces/state'
import { EpicRefined, Epic } from 'interfaces/middleware'
import { PAGE_CHANGED, SET_UNEXPECTED_ERROR, GlobalAction } from 'actions/globalActions'
import { APPEND_PEER_GROUP_RELATION, DELETE_RELATION, SET_PEER_GROUPS, SELECT_RELATION, PeerGroupActions } from 'actions/peergroupActions'
import {
  APPEND_MONEYFLOW_TRACKER,
  CHANGE_TRANSACTION_CATEGORY,
  DELETE_MONEYFLOW_TRACKER,
  GET_MONEYFLOW_TRACKERS,
  MoneyflowActions,
} from 'actions/moneyflowActions'
import { FORM_FIELD_CHANGE, FORM_SUBMITTED_SUCCESSFULLY, FormActions } from 'actions/formActions'
import { TOGGLE_DISMISS_INSIGHT, INSIGHT_CTA_CLICK, INSIGHT_SHOWN, InsightsActions } from 'actions/insightsActions'
import { REGISTER_SHARE, SocialActions } from 'actions/socialActions'
import { SLUGS_ANALYTICS_MAP } from 'constants/peergroups'
import { IdRangeEntity } from 'interfaces'
import { CategoryId, TypedMoneyflowCategory } from 'interfaces/meta'
import { InsightCTAKey, InsightSlug } from 'interfaces/insights'
import { ShareMethod } from 'interfaces/share'
import { RecaptchaPlacement } from 'interfaces/recaptcha'
import { MoneyflowType, Transaction } from 'interfaces/moneyflow'
import {
  FORM_EDIT_INCOME_TRANSACTION,
  FORM_EDIT_SPENDING_TRANSACTION,
  FORM_LOGIN,
  FORM_ONBOARDING_PROPERTY,
  FORM_ONBOARDING_REGISTER,
  FORM_PASSWORD_RESET,
  FORM_EDIT_TRANSACTIONS_TRANSACTION,
} from 'constants/forms'
import { DASHBOARD_URL, LOGIN_URL, ONBOARDING_REGISTER_URL } from 'tangle-shared'
import { getMemberRelation } from 'helpers/peergroupsHelpers'
import { Peergroup } from 'interfaces/peergroups'
import { LOGGED_IN, LOGGED_OUT, AuthActions } from 'actions/authActions'
import { SET_MEMBER_INFO, MemberActions } from 'actions/memberActions'
import { isAjaxError } from 'helpers/errorHelpers'
import { onboardingStepChanged } from 'epics/onboardingEpic'
import { RECAPTCHA_EXECUTED, RECAPTCHA_FAILED, RecaptchaActions } from 'actions/recaptchaActions'
import { getFriendlyCategoryTypeName } from 'helpers/categoriesHelpers'
import { TransactionEditFormValues } from 'interfaces/form'
import { DATE_FORMAT_ISO_FULL, DATE_FORMAT_US } from 'constants/datetime'
import { sendAnalytics } from 'helpers/utilsHelpers'
import NotificationActions, { NOTIFICATIONS_EXPAND_BUTTON_SHOWN, TOGGLE_NOTIFICATIONS_EXPANDED } from 'actions/notificationsActions'
import OfferActions, { OFFER_APPLY, OFFER_SHOWN } from 'actions/offersActions'
import { getPlatform } from 'helpers/userAgentHelpers'
import { TRACK_UI_EVENT, AnalyticsAction } from 'actions/analyticsActions'
import { byType } from 'rxOperators/byType'
import { filterRequestOfType } from 'rxOperators/filterRequestOfType'
import { end } from 'rxOperators/end'
import { formSubmitted, formSubmitFailed } from 'rxOperators/formsLegacy'
import { CREATE_STRIPE_PAYMENT_METHOD, CreateStripePaymentMethod } from 'actions/stripeActions'
import { AjaxCatchExtraActions } from 'rxOperators/ajaxCatch'
import FloatingPaneAction, { MODIFY_FLOATING_PANE } from 'actions/floatingPanesActions'

type AnalyticsOnboardingStep = 'register' | 'property'
type AnalyticsUnexpectedErrorKind = 'internal' | 'unhandled-api'

// format range for sending to analytics
const formatRange = (range: IdRangeEntity | null): string => {
  if (!range) {
    return '-'
  }

  const { upper, lower } = range.value
  if (!Number.isFinite(lower)) {
    return `${ upper }-`
  }

  if (!Number.isFinite(upper)) {
    return `${ lower }+`
  }

  return `${ lower }-${ upper }`
}

const DIMENSION_MAP = {
  login: 'dimension1',
  ageGroup: 'dimension2',
  incomeGroup: 'dimension3',
  housingTypeGroup: 'dimension5',
  geoGroup: 'dimension6',
  unexpectedError: 'dimension7',
  platform: 'dimension8',
}

const METRICS_MAP = {
  activeTrackers: 'metric1',
  onboardingStep1Starts: 'metric2',
  onboardingStep2Starts: 'metric3',
  onboardingStep2Submits: 'metric10',
  onboardingStartsKBA1: 'metric6',
}

const ONBOARDING_STEP_START_METRICS_MAP: Record<AnalyticsOnboardingStep, string> = {
  register: METRICS_MAP.onboardingStep1Starts,
  property: METRICS_MAP.onboardingStep2Starts,
}

type MetricOrDimensionKey = keyof typeof DIMENSION_MAP | keyof typeof METRICS_MAP

type ChangeTransactionCategoryMode = 'transaction-readonly' | 'transaction-form'

// in future it can be extracted to separate service and tested
const Analytics = {
  setDimensions () {
    const platform = getPlatform()

    if (platform) {
      sendAnalytics('set', DIMENSION_MAP.platform, platform)
    }
  },

  // first page view is tracked in index.html
  pageview (path: string) {
    sendAnalytics('set', 'page', path)
    sendAnalytics('send', 'pageview')
    fbq('track', 'PageView')
  },

  sendNonInteractionEvent (params?: Partial<Record<MetricOrDimensionKey, any>>) {
    sendAnalytics('send', 'event', 'non-interaction', 'non-interaction', {
      ...params,
      nonInteraction: true,
    })
  },

  peergroupDimensions (peergroup: Peergroup) {
    const { age_range, income_range, housing_type, location } = peergroup

    sendAnalytics('set', DIMENSION_MAP.ageGroup, formatRange(age_range))
    sendAnalytics('set', DIMENSION_MAP.incomeGroup, formatRange(income_range))
    sendAnalytics('set', DIMENSION_MAP.housingTypeGroup, housing_type || '-')
    sendAnalytics('set', DIMENSION_MAP.geoGroup, location ? location.name : '-')
    Analytics.sendNonInteractionEvent()
  },

  resetPeergroupDimensions () {
    sendAnalytics('set', DIMENSION_MAP.ageGroup, null)
    sendAnalytics('set', DIMENSION_MAP.incomeGroup, null)
    sendAnalytics('set', DIMENSION_MAP.housingTypeGroup, null)
    sendAnalytics('set', DIMENSION_MAP.geoGroup, null)
  },

  activeTrackers (count: number) {
    Analytics.sendNonInteractionEvent({
      [METRICS_MAP.activeTrackers]: count,
    })
  },

  createRelation () {
    sendAnalytics('send', 'event', 'custom-group', 'create')
  },

  deleteRelation () {
    sendAnalytics('send', 'event', 'custom-group', 'remove')
  },

  selectRelation (type: string) {
    sendAnalytics('send', 'event', 'peer-group', 'select', type)
  },

  setTracker (category: CategoryId) {
    sendAnalytics('send', 'event', 'tracker', 'set', category)
  },

  deleteTracker (category: CategoryId) {
    sendAnalytics('send', 'event', 'tracker', 'delete', category)
  },

  resetPassword () {
    sendAnalytics('send', 'event', 'password', 'reset')
  },

  startLogin () {
    sendAnalytics('send', 'event', 'login', 'start')
  },

  submitLogin (success: boolean) {
    const label = success ? 'success' : 'fail'
    sendAnalytics('send', 'event', 'login', 'submit', label)
  },

  startOnboarding (step: AnalyticsOnboardingStep) {
    const metricKey = ONBOARDING_STEP_START_METRICS_MAP[step]
    sendAnalytics('send', 'event', 'onboarding', 'start', step, {
      [metricKey]: 1,
    })
  },

  // Knowledge Based Authentication, credit questions
  startOnboardingKBA () {
    sendAnalytics('send', 'event', 'onboarding', 'start', 'kba', {
      [METRICS_MAP.onboardingStartsKBA1]: 1,
    })
  },

  submitOnboarding (step: AnalyticsOnboardingStep, success: boolean) {
    const action = success ? 'submit' : 'fail'
      ? { [METRICS_MAP.onboardingStep2Submits]: 1 }
      : {}
    sendAnalytics('send', 'event', 'onboarding', action, step)
  },


  notificationsImpression () {
    sendAnalytics('send', 'event', 'notifications tray', 'impression')
  },

  notificationsExpand (isExpanded: boolean) {
    sendAnalytics('send', 'event', 'notifications tray', isExpanded ? 'expand' : 'collapse')
  },

  insightImpression (slug: InsightSlug) {
    sendAnalytics('send', 'event', 'insights', 'impression', slug)
  },

  insightDismiss (slug: InsightSlug, is_dismissed: boolean) {
    sendAnalytics('send', 'event', 'insights', is_dismissed ? 'dismiss' : 'undismiss', slug)
  },

  // track insight clicks by slug
  insightClick (slug: InsightSlug) {
    sendAnalytics('send', 'event', 'insights', 'click', slug)
  },

  // track insight clicks by CTA kind
  insightCtaClick (ctaKind: InsightCTAKey) {
    // actually this event category should be 'insight-cta', but it cannot be renamed
    sendAnalytics('send', 'event', 'insight', 'click', ctaKind)
  },

  socialShare (network: ShareMethod) {
    sendAnalytics('send', 'event', 'social', 'share', network)
  },

  recaptchaExecuted (label: RecaptchaPlacement) {
    sendAnalytics('send', 'event', 'recaptcha', 'executed', label)
  },

  recaptchaResponse (isSuccessful: boolean, label: RecaptchaPlacement) {
    sendAnalytics('send', 'event', 'recaptcha', isSuccessful ? 'pass' : 'fail', label)
  },

  setLogin (status: 'yes' | 'no') {
    sendAnalytics('set', DIMENSION_MAP.login, status)
    Analytics.sendNonInteractionEvent()
  },

  unexpectedError (kind: AnalyticsUnexpectedErrorKind) {
    sendAnalytics('send', 'event', 'error', 'occur', kind, {
      [DIMENSION_MAP.unexpectedError]: 1,
    })
  },

  transactionRecategorized (prevCategory: TypedMoneyflowCategory, nextCategory: TypedMoneyflowCategory, mode: ChangeTransactionCategoryMode): void {
    const categoryTypeChange =
      `${ getFriendlyCategoryTypeName(prevCategory) } → ${ getFriendlyCategoryTypeName(nextCategory) }`
    const categoryChange = `${ prevCategory.value } → ${ nextCategory.value }`

    const analyticsRoot = `transaction recategorization${ mode === 'transaction-form' ? ' (form)' : '' }`

    sendAnalytics('send', 'event', analyticsRoot, categoryTypeChange, categoryChange)
  },

  transactionEdited (prevTransaction: Transaction, formValues: TransactionEditFormValues): void {
    // description changed if either prev or next string is not falsy and they are not equal
    const descriptionChanged = prevTransaction.description !== formValues.description
      && (Boolean(prevTransaction.description) || Boolean(formValues.description))
    const dateChanged = prevTransaction.date
      !== moment(formValues.date, DATE_FORMAT_US).format(DATE_FORMAT_ISO_FULL)

    const changesMap: Partial<Record<keyof TransactionEditFormValues, boolean>> = {
      date: dateChanged,
      description: descriptionChanged,
      amount: prevTransaction.amount !== numeral(formValues.amount).value(),
      category: prevTransaction.category.id !== formValues.category.id,
    }

    const changeHash = Object.keys(omitBy(changesMap, (value) => !value)).sort().join(', ')
    const applyToAllString = `apply locally`

    sendAnalytics('send', 'event', 'transaction edit', changeHash, applyToAllString)
  },

  offerImpression (id: string) {
    sendAnalytics('send', 'event', 'offers', 'impression', id)
  },

  offerApply (id: string) {
    sendAnalytics('send', 'event', 'offers', 'apply', id)
  },

  trackUIEvent (category: string, action: string | null, label?: string | void | null) {
    sendAnalytics('send', 'event', category, action, label)
  },

  premiumSubscriptionSuccess (
    subscriptionCost: number,
  ): void {
    sendAnalytics('send', 'event', 'subscription', 'pay', '', subscriptionCost)

    fbq('track', 'Subscribe', {
      currency: 'USD',
      value: subscriptionCost,
    })

    gtag('event', 'conversion', {
      send_to: 'AW-836344135/fpuGCLDbp_0BEMey5o4D',
      value: subscriptionCost,
      currency: 'USD',
    })
  },

}

// const pageviewEpic: EpicRefined<GlobalAction | AjaxCatchExtraActions> = (action$) =>
const pageviewEpic: Epic = (action$) =>
  action$
    .pipe(byType(PAGE_CHANGED))
    .pipe(end((action) => {
      const { next, prev } = action.payload

      // first pageview is tracked in index.html
      if (prev) {
        Analytics.pageview(next)
      } else {
        Analytics.setDimensions()
      }
    }))

const loggedInEpic: EpicRefined<AjaxCatchExtraActions | AuthActions | MemberActions> = (action$) =>
  action$
    .pipe(byType(LOGGED_IN, SET_MEMBER_INFO))
    .pipe(end(() => Analytics.setLogin('yes')))

const loggedOutEpic: EpicRefined<AjaxCatchExtraActions | AuthActions> = (action$) =>
  action$
    .pipe(byType(LOGGED_OUT))
    .pipe(end(() => {
      Analytics.setLogin('no')
      Analytics.resetPeergroupDimensions()
    }))

const peergroupDimensionsEpic: EpicRefined<AjaxCatchExtraActions | PeerGroupActions> = (action$) =>
  action$
    .pipe(byType(SET_PEER_GROUPS))
    .pipe(end(({ payload }) => {
      const memberRelation = getMemberRelation(payload.relations)
      if (memberRelation) {
        const peergroup = payload.peergroups.byKey[memberRelation.peergroupId]
        Analytics.peergroupDimensions(peergroup)
      }
    }))

const getTrackersObservable = (type: MoneyflowType) =>
  (action$: ActionsObservable<GlobalAction | AjaxCatchExtraActions | MoneyflowActions>, store: Store): Observable<number> => {
    const trackers = store.getState().data[type].trackers
    return trackers ?
      of(trackers.allKeys.length) :
      action$
        .pipe(filterRequestOfType(GET_MONEYFLOW_TRACKERS, 'success')).pipe(
          filter((a) => a.payload.params.type === type),
          map((a) => a.payload.response.allKeys.length),
          first())
  }

const activeTrackersEpic: EpicRefined<GlobalAction | AjaxCatchExtraActions | MoneyflowActions> = (action$, store) => action$.pipe(
  byType(PAGE_CHANGED),
  filter((a) => a.payload.next === DASHBOARD_URL),
  mergeMap(() =>
    combineLatest(
      getTrackersObservable('income')(action$, store),
      getTrackersObservable('spending')(action$, store)
    ).pipe(
      map(sum),
      end((count) => Analytics.activeTrackers(count))
    )
  ),
)

const createRelationEpic: EpicRefined<AjaxCatchExtraActions | PeerGroupActions> = (action$) =>
  action$
    .pipe(byType(APPEND_PEER_GROUP_RELATION))
    .pipe(end(Analytics.createRelation))

const deleteRelationEpic: EpicRefined<AjaxCatchExtraActions | PeerGroupActions> = (action$) =>
  action$
    .pipe(byType(DELETE_RELATION))
    .pipe(end(Analytics.deleteRelation))

const selectRelationEpic: EpicRefined<AjaxCatchExtraActions | PeerGroupActions> = (action$, store) =>
  action$
    .pipe(byType(SELECT_RELATION))
    .pipe(end(({ payload }) => {
      const { slug } = store.getState().data.peergroups.relations.byKey[payload]
      const groupType = slug && SLUGS_ANALYTICS_MAP[slug]
        ? SLUGS_ANALYTICS_MAP[slug]
        : 'custom'

      Analytics.selectRelation(groupType)
    }))

const setTrackerEpic: EpicRefined<AjaxCatchExtraActions | MoneyflowActions> = (action$) =>
  action$
    .pipe(byType(APPEND_MONEYFLOW_TRACKER))
    .pipe(end(({ payload }) => Analytics.setTracker(payload.data.category)))

const deleteTrackerEpic: EpicRefined<AjaxCatchExtraActions | MoneyflowActions> = (action$) =>
  action$
    .pipe(filterRequestOfType(DELETE_MONEYFLOW_TRACKER, 'success'))
    .pipe(end((a) => {
      Analytics.deleteTracker(a.payload.params.tracker.category)
    }))

const resetPasswordEpic: EpicRefined<AjaxCatchExtraActions | FormActions> = (action$) =>
  action$
    .pipe(formSubmitted(FORM_PASSWORD_RESET))
    .pipe(end(Analytics.resetPassword))

const startLoginEpic: EpicRefined<GlobalAction | FormActions | AjaxCatchExtraActions> = (action$) =>
  action$
    .pipe(byType(PAGE_CHANGED)).pipe(
      filter((a) => a.payload.next === LOGIN_URL),
      mergeMap(() =>
        action$
          .pipe(byType(FORM_FIELD_CHANGE)).pipe(
            filter((a) => a.meta.form === FORM_LOGIN),
            first(),
            takeUntil(
              action$
                .pipe(byType(PAGE_CHANGED)).pipe(
                  filter((a) => a.payload.prev === LOGIN_URL))
            ))
          .pipe(end(Analytics.startLogin))
      ))

const successSubmitLoginEpic: EpicRefined<FormActions | AjaxCatchExtraActions> = (action$) =>
  action$
    .pipe(byType(FORM_SUBMITTED_SUCCESSFULLY)).pipe(
      filter((a) => a.payload.formName === FORM_LOGIN))
    .pipe(end(() => Analytics.submitLogin(true)))

const failSubmitLoginEpic: EpicRefined<FormActions | AjaxCatchExtraActions> = (action$) =>
  action$
    .pipe(formSubmitFailed(FORM_LOGIN))
    .pipe(end(() => Analytics.submitLogin(false)))

const startOnboardingCoachingPaneEpic: EpicRefined<FloatingPaneAction | AjaxCatchExtraActions> = (action$) => action$.pipe(
  byType(MODIFY_FLOATING_PANE),
  mergeMap((action) => {
    const { payload } = action
    if (payload.type === 'advisor' && payload.mode === 'request' && payload.step === 'signup') {
      Analytics.startOnboarding('register')
    }

    return EMPTY
  })
)

const startOnboardingEpic: EpicRefined<GlobalAction | AjaxCatchExtraActions | FormActions> = (action$) => {
  const register$ = action$.pipe(
    byType(PAGE_CHANGED),
    filter((a) => a.payload.next === ONBOARDING_REGISTER_URL),
    mergeMap(() => action$.pipe(
      byType(FORM_FIELD_CHANGE),
      filter((a) => a.meta.form === FORM_ONBOARDING_REGISTER),
      first(),
      takeUntil(
        action$
          .pipe(byType(PAGE_CHANGED)).pipe(
            filter((a) => a.payload.prev === ONBOARDING_REGISTER_URL))
      ),
      end(() => Analytics.startOnboarding('register')),
    )
    ),
  )

  const others$ = action$.pipe(
    formSubmitted(FORM_ONBOARDING_REGISTER, FORM_ONBOARDING_PROPERTY),
    end(({ payload }) => {
      if (payload.formName === FORM_ONBOARDING_REGISTER) {
        Analytics.recaptchaResponse(true, 'register')
        Analytics.startOnboarding('property')
      }
    }),
  )

  return merge(
    register$,
    others$,
  )
}

const submitOnboardingFormEpic: EpicRefined<AjaxCatchExtraActions | FormActions> = (action$) => {
  const forms$ = action$
    .pipe(formSubmitted(FORM_ONBOARDING_REGISTER, FORM_ONBOARDING_PROPERTY))
    .pipe(end(({ payload }) => {
      if (payload.formName === FORM_ONBOARDING_REGISTER) {
        Analytics.submitOnboarding('register', true)
      }

      if (payload.formName === FORM_ONBOARDING_PROPERTY) {
        Analytics.submitOnboarding('property', true)
      }
    }))

  const finish$ = onboardingStepChanged(action$).pipe(
    filter((a) => a.payload.onboarding_step === 'finished'))
    .pipe(end(() => {}))

  return merge(
    forms$,
    finish$,
  )
}

const failSubmitOnboardingFormEpic: EpicRefined<AjaxCatchExtraActions | FormActions> = (action$) => {
  const register$ = action$
    .pipe(formSubmitFailed(FORM_ONBOARDING_REGISTER))
    .pipe(end(() => Analytics.submitOnboarding('register', false)))

  const property$ = action$
    .pipe(formSubmitFailed(FORM_ONBOARDING_PROPERTY))
    .pipe(end(() => Analytics.submitOnboarding('property', false)))

  return merge(
    register$,
    property$,
  )
}

const insightImpressionEpic: EpicRefined<AjaxCatchExtraActions | InsightsActions> = (action$) =>
  action$
    .pipe(byType(INSIGHT_SHOWN))
    .pipe(end(({ payload }) => {
      Analytics.insightImpression(payload.slug)
    }))

const insightDismissEpic: EpicRefined<AjaxCatchExtraActions | InsightsActions> = (action$) =>
  action$
    .pipe(byType(TOGGLE_DISMISS_INSIGHT))
    .pipe(end(({ payload }) => {
      Analytics.insightDismiss(payload.insight.slug, payload.is_dismissed)
    }))

const insightCtaClickEpic: EpicRefined<AjaxCatchExtraActions | InsightsActions> = (action$) =>
  action$
    .pipe(byType(INSIGHT_CTA_CLICK))
    .pipe(end(({ payload }) => {
      if (!payload.insight) {
        return
      }

      Analytics.insightCtaClick(payload.type)
      Analytics.insightClick(payload.insight.slug)
    }))

const socialShareEpic: EpicRefined<SocialActions | AjaxCatchExtraActions> = (action$) =>
  action$
    .pipe(byType(REGISTER_SHARE))
    .pipe(end(({ payload }) => Analytics.socialShare(payload.method)))

const recaptchaExecutedEpic: EpicRefined<RecaptchaActions | AjaxCatchExtraActions> = (action$) =>
  action$
    .pipe(byType(RECAPTCHA_EXECUTED))
    .pipe(end(({ payload: label }) => Analytics.recaptchaExecuted(label)))

const recaptchaFailedEpic: EpicRefined<RecaptchaActions | AjaxCatchExtraActions> = (action$) =>
  action$
    .pipe(byType(RECAPTCHA_FAILED))
    .pipe(end(({ payload: label }) => Analytics.recaptchaResponse(false, label)))

const unexpectedErrorEpic: EpicRefined<GlobalAction | AjaxCatchExtraActions> = (action$) =>
  action$
    .pipe(byType(SET_UNEXPECTED_ERROR)).pipe(
    // we need only internal errors that really unexpected
      filter((a) => Boolean(a.payload.internalError)),
      map((a): AnalyticsUnexpectedErrorKind =>
        a.payload.internalError && isAjaxError(a.payload.internalError)
          ? 'unhandled-api'
          : 'internal'
      ))
    .pipe(end(Analytics.unexpectedError))

const transactionRecategorizedEpic: EpicRefined<MoneyflowActions | AjaxCatchExtraActions> = (action$) =>
  action$
    .pipe(filterRequestOfType(CHANGE_TRANSACTION_CATEGORY, 'request'))
    .pipe(end((a) => {
      const { transaction, category: nextCategory } = a.payload.params
      const { category: prevCategory } = transaction

      Analytics.transactionRecategorized(prevCategory, nextCategory, 'transaction-readonly')
    }))

const transactionEditEpic: EpicRefined<AjaxCatchExtraActions | FormActions> = (action$) =>
  action$
    .pipe(formSubmitted(FORM_EDIT_SPENDING_TRANSACTION, FORM_EDIT_INCOME_TRANSACTION, FORM_EDIT_TRANSACTIONS_TRANSACTION))
    .pipe(end((a) => {
      if (a.payload.formName === FORM_EDIT_SPENDING_TRANSACTION || a.payload.formName === FORM_EDIT_INCOME_TRANSACTION || a.payload.formName === FORM_EDIT_TRANSACTIONS_TRANSACTION) {

        const { extra, formValues } = a.payload
        const prevCategory = extra.transaction.category
        const nextCategory = formValues.category
        if (prevCategory.id !== nextCategory.id) {
          Analytics.transactionRecategorized(prevCategory, nextCategory, 'transaction-form')
        }

        Analytics.transactionEdited(extra.transaction, formValues)
      }
    }))

const notificationsExpandButtonEpic: EpicRefined<AjaxCatchExtraActions | NotificationActions> = (action$) =>
  action$
    .pipe(byType(NOTIFICATIONS_EXPAND_BUTTON_SHOWN))
    .pipe(end(() => Analytics.notificationsImpression()))

const notificationsExpandEpic: EpicRefined<AjaxCatchExtraActions | NotificationActions> = (action$) =>
  action$
    .pipe(byType(TOGGLE_NOTIFICATIONS_EXPANDED))
    .pipe(end(({ payload }) => Analytics.notificationsExpand(payload.isExpanded)))

const offerImpressionEpic: EpicRefined<AjaxCatchExtraActions | OfferActions> = (action$) =>
  action$
    .pipe(byType(OFFER_SHOWN))
    .pipe(end(({ payload }) => {
      Analytics.offerImpression(payload.id)
    }))

const offerApplyEpic: EpicRefined<AjaxCatchExtraActions | OfferActions> = (action$) =>
  action$
    .pipe(byType(OFFER_APPLY))
    .pipe(end(({ payload }) => {
      Analytics.offerApply(payload.id)
    }))

const clickTrackerEpic: EpicRefined<AnalyticsAction | AjaxCatchExtraActions> = (action$) =>
  action$
    .pipe(byType(TRACK_UI_EVENT))
    .pipe(end(({ payload: { category, action, label }}) => Analytics.trackUIEvent(category, action, label)))

const subscriptionSuccessEpic: EpicRefined<CreateStripePaymentMethod> = (action$) => (
  action$.pipe(
    filterRequestOfType(CREATE_STRIPE_PAYMENT_METHOD, 'success'),
    mergeMap((action) => {
      const { payload: { params: { amount: subscriptionCost }}} = action
      Analytics.premiumSubscriptionSuccess(
        subscriptionCost,
      )
      return EMPTY
    }),
  )
)

export default combineEpics(
  pageviewEpic,
  loggedInEpic,
  loggedOutEpic,
  peergroupDimensionsEpic,
  activeTrackersEpic,
  createRelationEpic,
  deleteRelationEpic,
  selectRelationEpic,
  setTrackerEpic,
  deleteTrackerEpic,
  resetPasswordEpic,
  startLoginEpic,
  successSubmitLoginEpic,
  failSubmitLoginEpic,
  startOnboardingEpic,
  submitOnboardingFormEpic,
  failSubmitOnboardingFormEpic,
  insightImpressionEpic,
  insightDismissEpic,
  insightCtaClickEpic,
  socialShareEpic,
  recaptchaExecutedEpic,
  recaptchaFailedEpic,
  unexpectedErrorEpic,
  transactionRecategorizedEpic,
  transactionEditEpic,
  notificationsExpandButtonEpic,
  notificationsExpandEpic,
  offerImpressionEpic,
  offerApplyEpic,
  clickTrackerEpic,
  subscriptionSuccessEpic,
  startOnboardingCoachingPaneEpic,
)
